<script setup lang="ts">
import { ECheckpointActions } from '@/store/checkpointStore/CheckpointStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { ConfigType } from '@/types/models/PartnerConfigModel'
import type { PartnerModel } from '@/types/partners/PartnerModel'
import {
    BaseDialog,
    EditableTypedInput,
    InformationBaseCard,
    StackLayout,
    TypographyItem
} from '@rfc/rfc-components-library'
import { localFallback } from '@rfc/rfc-components-library/locales'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import EventCreateCheckpointInputs from './EventCreateCheckpointInputs.vue'

const store = useStore()
const i18n = useI18n()

type Props = {
    eventId: number
    open: boolean
    partnerConfig?: PartnerModel
}
const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'refresh'): void
    (e: 'close'): void
}>()
const editMode = ref(true)
const isSubmitted = ref(false)
const data = ref<Partial<CheckpointModel>>({ comments: '' })

const handleCancel = () => {
    emit('close')
}

const handleConfirm = () => {
    isSubmitted.value = true
    if (!data.value.name) return
    store
        ?.dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.CREATE_CHECKPOINT}`, {
            eventId: props.eventId,
            data: data.value
        })
        .then(() => {
            emit('refresh')
            isSubmitted.value = false
            data.value = { comments: '' }
        })
}
</script>

<template>
    <BaseDialog
        :open="open"
        :title="$t('event.detail.checkpoints.createCheckpointTitle')"
        :loading="!!store?.state.checkpoint.loading"
        class="md:w-2/3 lg:w-1/2 create-checkpoint-dialog"
        @confirm="handleConfirm"
        @close="handleCancel">
        <template #content>
            <StackLayout direction="column" :gap="16" class="pt-5 w-full">
                <EventCreateCheckpointInputs
                    :data="data"
                    :eventId="eventId"
                    :isSubmitted="isSubmitted"
                    :editMode="editMode"
                    @change="data = { ...data, ...$event }" />
                <InformationBaseCard
                    v-if="partnerConfig?.config?.checkpoint"
                    :label="$t('event.detail.checkpoints.partner')"
                    class="w-full">
                    <StackLayout direction="column" :gap="8" class="w-full">
                        <TypographyItem
                            :label="`${$t('event.detail.checkpoints.partner')} : ${partnerConfig.name}`" />
                        <EditableTypedInput
                            v-for="([itemKey, itemValue], key) in Object.entries(
                                partnerConfig?.config?.checkpoint
                            )"
                            :key="key"
                            editMode
                            :label="
                                (itemValue as ConfigType)?.label
                                    ? (localFallback((itemValue as ConfigType)?.label, i18n) ??
                                      itemKey)
                                    : itemKey
                            "
                            :dataType="(itemValue as ConfigType)?.type ?? (itemValue as string)"
                            @change="data.partner_config = { [itemKey]: $event }" />
                    </StackLayout>
                </InformationBaseCard>
            </StackLayout>
        </template>
    </BaseDialog>
</template>
